import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { PaywallDialog } from "@/components/PaywallDialog";

interface SMB1001State {
  enabled: "enabled" | "disabled";
  level?: "Bronze" | "Silver" | "Gold" | "Platinum" | "Diamond";
  Level?: "Bronze" | "Silver" | "Gold" | "Platinum" | "Diamond";
}

const defaultSMB1001: SMB1001State = {
  enabled: "disabled",
  level: "Bronze",
  Level: "Bronze",
};

export default function SMB1001Module() {
  const [smb1001, setSMB1001] = useState<SMB1001State>(defaultSMB1001);
  const [smb1001Initial, setSMB1001Initial] = useState<SMB1001State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    const fetchSMB1001 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const smb1001Settings = settings?.["smb1001"] || settings?.["SMB1001"] || {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof smb1001Settings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(smb1001Settings.Enabled);
        } else if (typeof smb1001Settings.enabled !== 'undefined') {
          isEnabled = checkEnabled(smb1001Settings.enabled);
        } else if (typeof smb1001Settings.active !== 'undefined') {
          isEnabled = checkEnabled(smb1001Settings.active);
        } else if (typeof smb1001Settings.Active !== 'undefined') {
          isEnabled = checkEnabled(smb1001Settings.Active);
        }
        let level: SMB1001State["level"] = "Bronze";
        if (typeof smb1001Settings.Level !== 'undefined') {
          level = smb1001Settings.Level;
        } else if (typeof smb1001Settings.level !== 'undefined') {
          level = smb1001Settings.level;
        }
        console.log('[SMB1001Module] Level:', level);
        setSMB1001({ enabled: isEnabled ? "enabled" : "disabled", level: level });
        setSMB1001Initial({ enabled: isEnabled ? "enabled" : "disabled", level: level });
      } catch (err) {
        toast.error("Failed to fetch SMB 1001 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSMB1001();
  }, []);

  const smb1001Changed = smb1001Initial && JSON.stringify(smb1001) !== JSON.stringify(smb1001Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        smb1001: { 
          Enabled: smb1001.enabled === "enabled",
          Level: smb1001.level,
         },
      });
      setSMB1001Initial({ ...smb1001 });
      toast.success("SMB 1001 settings saved");
      
      // DED-1511: Dispatch event to notify sidebar of SMB1001 level change
      window.dispatchEvent(new CustomEvent('smb1001LevelChanged', { 
        detail: { 
          level: smb1001.enabled === "enabled" ? smb1001.level : '',
          enabled: smb1001.enabled === "enabled"
        } 
      }));
    } catch (err) {
      toast.error("Failed to save SMB 1001 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="SMB 1001">📊</span>
        </div>
        <div>
          <div className="font-semibold text-lg">SMB 1001</div>
          <div className="text-xs text-muted-foreground">SMB 1001 Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={smb1001.enabled}
          onValueChange={val => setSMB1001(v => ({ ...v, enabled: val as SMB1001State["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* SMB Level Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">SMB Level</label>
        <Select
          value={smb1001.level}
          onValueChange={(val) => {
            const level = val as SMB1001State["level"];
            if (level === "Platinum" || level === "Diamond") {
              setShowPaywall(true);
            } else {
              setSMB1001(v => ({ ...v, level }));
            }
          }}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Bronze">Bronze</SelectItem>
            <SelectItem value="Silver">Silver</SelectItem>
            <SelectItem value="Gold">Gold</SelectItem>
            <SelectItem value="Platinum">Platinum (Premium)</SelectItem>
            <SelectItem value="Diamond">Diamond (Premium)</SelectItem>
          </SelectContent>
        </Select>
      </div>
     
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!smb1001Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save SMB 1001 Settings"}
        </Button>
      </div>

      <PaywallDialog open={showPaywall} onOpenChange={setShowPaywall} />
    </div>
  );
}
